import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbCarousel } from '@ng-bootstrap/ng-bootstrap';
import { NgbSlideEvent } from '@ng-bootstrap/ng-bootstrap/carousel/carousel';
import { CmsService } from '@spartacus/core';
import { CmsComponentData } from '@spartacus/storefront';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { CmsBannerComponent, CmsTimedBannerCarouselComponent } from '../../../core/model';

const MIN_DURATION = 2500; // 2.5 seconds
const MAX_DURATION = 60000; // 1 minute

@Component({
  selector: 'py-timed-banner-collection',
  templateUrl: './timed-banner-collection.component.html',
  styleUrls: ['./timed-banner-collection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TimedBannerCollectionComponent implements OnInit, OnDestroy {
  @ViewChild('carousel', { static: false }) carousel: NgbCarousel;

  duration: number;
  timeLeft: number;
  styleClasses: string;
  paused: boolean = false;
  banners: CmsBannerComponent[];
  activeSlide: string = 'slide-0';
  isOnlyOneBanner: boolean;

  subscriptions = new Subscription();

  constructor(
    public cmsComponent: CmsComponentData<CmsTimedBannerCarouselComponent>,
    private cmsService: CmsService,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.cmsComponent.data$
        .pipe(
          filter((data) => !!data),
          tap((data) => {
            this.styleClasses = data.styleClasses;
            this.setDuration(data.duration);
          }),
          map((data) => data.banners?.split(' ') || []),
          map((bannerIds) => this.getBannersData(bannerIds)),
          switchMap((banners: Array<Observable<CmsBannerComponent>>) => combineLatest(banners))
        )
        .subscribe((banners) => {
          this.banners = banners;
          this.isOnlyOneBanner = banners?.length === 1;
          this.cd.markForCheck();
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  goToSlide(value: number | string): void {
    if (value === 'prev') {
      this.carousel.prev();
    } else if (value === 'next') {
      this.carousel.next();
    } else {
      this.carousel.select(`slide-${value}`);
    }
  }

  togglePauseAndPlay(): void {
    if (this.paused) {
      this.carousel.interval = this.timeLeft;
      this.carousel.cycle();
    } else {
      this.carousel.pause();
    }
    this.paused = !this.paused;
  }

  onSlide(slideEvent: NgbSlideEvent): void {
    if (this.carousel.interval !== this.duration) {
      this.carousel.interval = this.duration;
      this.timeLeft = this.duration;
    }
    this.paused = slideEvent.paused;
    this.activeSlide = slideEvent.current;
  }

  private setDuration(value: string): void {
    let duration = Number(value || '0');
    if (duration < MIN_DURATION) {
      duration = MIN_DURATION;
    } else if (duration > MAX_DURATION) {
      duration = MAX_DURATION;
    }
    this.duration = duration;
  }

  private getBannersData(bannerIds: string[]): Array<Observable<CmsBannerComponent>> {
    return bannerIds.map((bannerId) => this.cmsService.getComponentData<CmsBannerComponent>(bannerId).pipe(filter(Boolean)));
  }
}
